import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from '@clerk/clerk-react';
import { toast } from 'sonner';
import { chatService } from '@/lib/api/services/chat.service';
import { useSubscribeToConversation } from '@/lib/websocket/realtime-chat';

export function useConversations() {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const queryKey = ['conversations'];

  // Fetch conversations
  const {
    data: conversations = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      return await chatService.getConversations();
    },
    enabled: !!user?.id,
    refetchInterval: (query) => {
      // Fallback to polling if WebSocket fails
      const hasError = query.state.error;
      if (hasError && !document.hidden) {
        return 30000; // Poll every 30 seconds as fallback
      }
      return false; // Use WebSocket for real-time updates
    },
  });

  // Create or find conversation mutation
  const createConversationMutation = useMutation({
    mutationFn: async (otherUserId: string) => {
      return await chatService.createOrFindConversation(otherUserId);
    },
    onSuccess: (data) => {
      // Invalidate conversations list
      queryClient.invalidateQueries({ queryKey });
      // Also set the new conversation in cache
      queryClient.setQueryData(['conversation', data.id], data);
      toast.success('Conversation started');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create conversation');
      console.error('Error creating conversation:', error);
    },
  });

  return {
    conversations,
    isLoading,
    error,
    refetch,
    createConversation: createConversationMutation.mutateAsync,
    isCreating: createConversationMutation.isPending,
  };
}

export function useConversation(conversationId: string | null) {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const queryKey = ['conversation', conversationId];

  const {
    data: conversation,
    isLoading,
    error,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!conversationId) return null;
      return await chatService.getConversation(conversationId);
    },
    enabled: !!conversationId && !!user?.id,
    refetchInterval: false, // Disable polling - use WebSocket for real-time updates
  });

  // Subscribe to conversation updates via WebSocket
  useSubscribeToConversation(
    conversationId,
    () => {
      // Invalidate conversation query when updated
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    (error) => {
      console.error('Error in conversation subscription:', error);
    }
  );

  return {
    conversation,
    isLoading,
    error,
  };
}
